import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Copy, Check, Search, Filter, ExternalLink, Shield, Network, Table, Grid3X3 } from "lucide-react";
import { toast } from "../../components/ui/use-toast";
import NavBar from '../../components/NavBar';

interface WADComsCommand {
  command: string;
  description: string;
  category: string;
  os: string;
  attackType: string;
  services: string[];
  credentials: string[];
}

interface WADComsEntry {
  name: string;
  description: string;
  commands: WADComsCommand[];
  url: string;
}

export const WADComsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wadcomsData, setWadcomsData] = useState<WADComsEntry[]>([]);
  const [filteredData, setFilteredData] = useState<WADComsEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCredentials, setSelectedCredentials] = useState<string>("all");
  const [selectedServices, setSelectedServices] = useState<string>("all");
  const [selectedAttackType, setSelectedAttackType] = useState<string>("all");
  const [selectedOS, setSelectedOS] = useState<string>("all");
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [wadcomsEnabled, setWadcomsEnabled] = useState<boolean | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await checkWADComsAccess();
        await loadWADComsData();
      } else {
        navigate('/signin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const checkWADComsAccess = async () => {
    try {
      const settings = await methodologyService.getSystemSettings();
      setWadcomsEnabled(!settings.disableWADComs);
      
      if (settings.disableWADComs) {
        setError("WADComs is currently disabled by the administrator.");
      }
    } catch (error) {
      console.error("Error checking WADComs access:", error);
      setError("Failed to check WADComs access.");
    }
  };

  const loadWADComsData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/wadcoms.json');
      const data: WADComsEntry[] = await response.json();
      setWadcomsData(data);
      setFilteredData(data);
    } catch (error) {
      console.error("Error loading WADComs data:", error);
      setError("Failed to load WADComs data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCommand = async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(command);
      toast({
        title: "Command Copied",
        description: "Command has been copied to clipboard.",
      });
      setTimeout(() => setCopiedCommand(null), 2000);
    } catch (error) {
      console.error("Failed to copy command:", error);
      toast({
        title: "Copy Failed",
        description: "Failed to copy command to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Filter data based on search and filters
  useEffect(() => {
    let filtered = wadcomsData;

    if (searchTerm) {
      filtered = filtered.filter(entry => 
        (entry.name && entry.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (entry.description && entry.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        entry.commands.some(cmd => 
          (cmd.command && cmd.command.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (cmd.description && cmd.description.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
    }

    if (selectedCredentials !== "all") {
      filtered = filtered.filter(entry =>
        entry.commands.some(cmd =>
          Array.isArray(cmd.credentials) && cmd.credentials.map(c => c.toLowerCase()).includes(selectedCredentials.toLowerCase())
        )
      );
    }

    if (selectedServices !== "all") {
      filtered = filtered.filter(entry =>
        entry.commands.some(cmd =>
          Array.isArray(cmd.services) && cmd.services.map(s => s.toLowerCase()).includes(selectedServices.toLowerCase())
        )
      );
    }

    if (selectedAttackType !== "all") {
      filtered = filtered.filter(entry =>
        entry.commands.some(cmd =>
          (cmd.attackType || '').toLowerCase() === selectedAttackType.toLowerCase()
        )
      );
    }

    if (selectedOS !== "all") {
      filtered = filtered.filter(entry =>
        entry.commands.some(cmd =>
          (cmd.os || '').toLowerCase() === selectedOS.toLowerCase()
        )
      );
    }

    setFilteredData(filtered);
  }, [wadcomsData, searchTerm, selectedCredentials, selectedServices, selectedAttackType, selectedOS]);

  // Get unique values for filters
  const credentialsOptions = Array.from(
    new Set((wadcomsData || []).flatMap(entry => (entry.commands || []).flatMap(cmd => cmd.credentials || [])))
  ).filter(Boolean).sort();

  const servicesOptions = Array.from(
    new Set((wadcomsData || []).flatMap(entry => (entry.commands || []).flatMap(cmd => cmd.services || [])))
  ).filter(Boolean).sort();

  const attackTypeOptions = Array.from(
    new Set((wadcomsData || []).flatMap(entry => (entry.commands || []).map(cmd => cmd.attackType || '')))
  ).filter(Boolean).sort();

  const osOptions = Array.from(
    new Set((wadcomsData || []).flatMap(entry => (entry.commands || []).map(cmd => cmd.os || '')))
  ).filter(Boolean).sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#daed6e] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading WADComs data...</p>
        </div>
      </div>
    );
  }

  if (error || wadcomsEnabled === false) {
    return (
      <div className="min-h-screen bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-4">
            <Shield className="mx-auto h-12 w-12 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">{error || "WADComs is currently unavailable."}</p>
          <Button onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative overflow-x-hidden max-w-full">
      <NavBar userName={auth.currentUser?.displayName || ''} onSignOut={handleSignOut} />
      
      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12 overflow-x-hidden max-w-full">
        <div className="bg-white rounded-[20px] p-8 shadow-lg overflow-x-hidden max-w-full">
          {/* Mobile back button */}
          <div className="lg:hidden mb-4">
            <button 
              onClick={() => navigate('/toolkits')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
            >
              ← Back to Toolkits
            </button>
          </div>

          <div className="max-w-7xl mx-auto overflow-x-hidden max-w-full">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Network className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                    WADComs - Windows Active Directory Commands
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Windows Active Directory commands and techniques for security testing - {wadcomsData.length} entries available
                  </p>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white rounded-lg p-6 mb-6 border-2 border-[#E7E7E7] overflow-x-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-4 mb-6 overflow-x-hidden max-w-full">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search tools, commands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={selectedCredentials} onValueChange={setSelectedCredentials}>
                  <SelectTrigger>
                    <SelectValue placeholder="Credentials" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Credentials</SelectItem>
                    {credentialsOptions.map(cred => (
                      <SelectItem key={cred} value={cred}>{cred}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedServices} onValueChange={setSelectedServices}>
                  <SelectTrigger>
                    <SelectValue placeholder="Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {servicesOptions.map(service => (
                      <SelectItem key={service} value={service}>{service}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedAttackType} onValueChange={setSelectedAttackType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Attack Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Attack Types</SelectItem>
                    {attackTypeOptions.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedOS} onValueChange={setSelectedOS}>
                  <SelectTrigger>
                    <SelectValue placeholder="OS" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All OS</SelectItem>
                    {osOptions.map(os => (
                      <SelectItem key={os} value={os}>{os}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCredentials("all");
                    setSelectedServices("all");
                    setSelectedAttackType("all");
                    setSelectedOS("all");
                  }}
                  className="font-['Space_Grotesk'] text-black"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">View Mode:</span>
                  <div className="flex border rounded-lg">
                    <Button
                      variant={viewMode === 'cards' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('cards')}
                      className="rounded-r-none"
                    >
                      <Grid3X3 className="w-4 h-4 mr-2" />
                      Cards
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('table')}
                      className="rounded-l-none"
                    >
                      <Table className="w-4 h-4 mr-2" />
                      Table
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {filteredData.length} {filteredData.length === 1 ? 'entry' : 'entries'} found
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4 overflow-x-hidden">
              {filteredData.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mb-4">
                    <Network className="mx-auto h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                  <p className="text-gray-500">
                    Try adjusting your search terms or filters.
                  </p>
                </div>
              ) : viewMode === 'table' ? (
                // Table View
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-2 py-3 text-left font-semibold text-gray-900 min-w-[100px] max-w-[120px]">Tool</th>
                        <th className="border border-gray-300 px-2 py-3 text-left font-semibold text-gray-900 min-w-[80px] max-w-[100px]">Commands</th>
                        <th className="border border-gray-300 px-2 py-3 text-left font-semibold text-gray-900 min-w-[60px] max-w-[80px]">OS</th>
                        <th className="border border-gray-300 px-2 py-3 text-left font-semibold text-gray-900 min-w-[80px] max-w-[100px]">Attack Type</th>
                        <th className="border border-gray-300 px-2 py-3 text-left font-semibold text-gray-900 min-w-[120px]">Description</th>
                        <th className="border border-gray-300 px-2 py-3 text-left font-semibold text-gray-900 min-w-[80px] max-w-[100px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((entry, index) => (
                        <tr key={entry.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-300 px-2 py-3 font-medium text-gray-900 text-sm">
                            <div className="truncate" title={entry.name}>
                              {entry.name}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-2 py-3 text-gray-700">
                            <div className="space-y-1">
                              {entry.commands.slice(0, 2).map((cmd, cmdIndex) => (
                                <div key={cmdIndex} className="text-xs truncate" title={cmd.category}>
                                  {cmd.category}
                                </div>
                              ))}
                              {entry.commands.length > 2 && (
                                <div className="text-xs text-gray-500">+{entry.commands.length - 2} more</div>
                              )}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-2 py-3 text-gray-700 text-xs">
                            {entry.commands[0]?.os || 'Windows'}
                          </td>
                          <td className="border border-gray-300 px-2 py-3 text-gray-700">
                            <div className="space-y-1">
                              {entry.commands.slice(0, 2).map((cmd, cmdIndex) => (
                                <div key={cmdIndex} className="text-xs truncate" title={cmd.attackType}>
                                  {cmd.attackType}
                                </div>
                              ))}
                              {entry.commands.length > 2 && (
                                <div className="text-xs text-gray-500">+{entry.commands.length - 2} more</div>
                              )}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-2 py-3 text-gray-700 text-xs">
                            <div className="line-clamp-2" title={entry.description}>
                              {entry.description}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-2 py-3">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const commands = entry.commands.map(cmd => cmd.command).join('\n');
                                  handleCopyCommand(commands);
                                }}
                                title="Copy all commands"
                                className="p-1 h-8 w-8"
                              >
                                {copiedCommand === entry.commands.map(cmd => cmd.command).join('\n') ? (
                                  <Check size={12} className="text-green-600" />
                                ) : (
                                  <Copy size={12} />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(entry.url, '_blank')}
                                title="View details"
                                className="p-1 h-8 w-8"
                              >
                                <ExternalLink size={12} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Card View
                filteredData.map((entry) => (
                  <Card key={entry.name} className="border-2 border-[#E7E7E7]">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <CardTitle className="text-xl text-black truncate">{entry.name}</CardTitle>
                            <Badge variant="outline" className="text-xs flex-shrink-0">
                              {entry.commands.length} command{entry.commands.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{entry.description}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(entry.url, '_blank')}
                          className="flex-shrink-0 ml-2"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {entry.commands.map((command, index) => (
                          <div key={index} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <h4 className="font-semibold text-black truncate">{command.category}</h4>
                                  <Badge variant="secondary" className="text-xs flex-shrink-0">{command.os}</Badge>
                                  <Badge variant="outline" className="text-xs flex-shrink-0">{command.attackType}</Badge>
                                  {command.credentials.slice(0, 2).map((cred, idx) => (
                                    <Badge key={idx} variant="destructive" className="text-xs flex-shrink-0">{cred}</Badge>
                                  ))}
                                  {command.credentials.length > 2 && (
                                    <Badge variant="destructive" className="text-xs flex-shrink-0">+{command.credentials.length - 2}</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{command.description}</p>
                                <div className="flex flex-wrap gap-1">
                                  {command.services.slice(0, 3).map((service, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs flex-shrink-0">{service}</Badge>
                                  ))}
                                  {command.services.length > 3 && (
                                    <Badge variant="outline" className="text-xs flex-shrink-0">+{command.services.length - 3}</Badge>
                                  )}
                                </div>
                              </div>
                              <Button
                                onClick={() => handleCopyCommand(command.command)}
                                size="sm"
                                variant="outline"
                                className="ml-2 flex-shrink-0"
                              >
                                {copiedCommand === command.command ? (
                                  <Check size={16} className="mr-1 text-green-600" />
                                ) : (
                                  <Copy size={16} className="mr-1" />
                                )}
                                {copiedCommand === command.command ? 'Copied!' : 'Copy'}
                              </Button>
                            </div>
                            <div className="bg-black text-green-400 p-3 rounded text-sm font-mono overflow-x-auto break-all">
                              <code className="whitespace-pre-wrap">{command.command}</code>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Results count */}
            <div className="mt-6 text-center text-sm text-gray-500">
              Showing {filteredData.length} of {wadcomsData.length} entries
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 